import { useState } from 'react';
import { Plus, Database, Settings, FolderTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DimensionList } from './components/DimensionList';
import { DimensionBuilder } from './components/DimensionBuilder';
import { DimensionPreview } from './components/DimensionPreview';
import { DimensionSchemaConfig } from './types/dimension.types';

// Default dimensions for government data warehouse
const defaultDimensions: DimensionSchemaConfig[] = [
  {
    id: 'dim_date',
    name: 'Date',
    category: 'temporal',
    description: 'Dimension waktu untuk analisis tren historis',
    isSystem: true,
    icon: 'Calendar',
    fields: [
      { name: 'date', type: 'date', required: true, description: 'Tanggal transaksi' },
      { name: 'year', type: 'integer', required: true, description: 'Tahun' },
      { name: 'month', type: 'integer', required: true, description: 'Bulan (1-12)' },
      { name: 'quarter', type: 'integer', required: true, description: 'Quarter (1-4)' },
      { name: 'fiscal_year', type: 'integer', required: false, description: 'Tahun anggaran' },
      { name: 'is_holiday', type: 'boolean', required: false, description: 'Hari libur nasional' },
    ]
  },
  {
    id: 'dim_region',
    name: 'Region',
    category: 'geographic',
    description: 'Dimension wilayah administratif pemerintahan',
    isSystem: true,
    icon: 'MapPin',
    fields: [
      { name: 'province_code', type: 'string', length: 10, required: true, description: 'Kode provinsi (BPS)' },
      { name: 'province_name', type: 'string', length: 100, required: true, description: 'Nama provinsi' },
      { name: 'city_code', type: 'string', length: 10, required: true, description: 'Kode kabupaten/kota (BPS)' },
      { name: 'city_name', type: 'string', length: 100, required: true, description: 'Nama kabupaten/kota' },
      { name: 'district_code', type: 'string', length: 15, required: false, description: 'Kode kecamatan (BPS)' },
      { name: 'district_name', type: 'string', length: 100, required: false, description: 'Nama kecamatan' },
      { name: 'village_code', type: 'string', length: 20, required: false, description: 'Kode desa/kelurahan (BPS)' },
      { name: 'village_name', type: 'string', length: 100, required: false, description: 'Nama desa/kelurahan' },
    ]
  },
  {
    id: 'dim_program',
    name: 'Program',
    category: 'government',
    description: 'Dimension program/kegiatan pemerintahan',
    isSystem: true,
    icon: 'Briefcase',
    fields: [
      { name: 'program_code', type: 'string', length: 50, required: true, description: 'Kode program (RKPD)' },
      { name: 'program_name', type: 'string', length: 200, required: true, description: 'Nama program' },
      { name: 'activity_code', type: 'string', length: 50, required: true, description: 'Kode kegiatan' },
      { name: 'activity_name', type: 'string', length: 200, required: true, description: 'Nama kegiatan' },
      { name: 'budget_year', type: 'integer', required: true, description: 'Tahun anggaran' },
      { name: 'pagu', type: 'decimal', precision: 18, scale: 2, required: false, description: 'Pagu anggaran' },
    ]
  },
  {
    id: 'dim_department',
    name: 'Department',
    category: 'organizational',
    description: 'Dimension organisasi/perangkat daerah',
    isSystem: true,
    icon: 'Building',
    fields: [
      { name: 'skpd_code', type: 'string', length: 20, required: true, description: 'Kode SKPD' },
      { name: 'skpd_name', type: 'string', length: 200, required: true, description: 'Nama SKPD' },
      { name: 'department_level', type: 'enum', values: ['provinsi', 'kabupaten', 'kecamatan'], required: true, description: 'Level instansi' },
      { name: 'head_of_office', type: 'string', length: 100, required: false, description: 'Kepala dinas' },
    ]
  },
];

export default function DimensionManagementPage() {
  const [dimensions, setDimensions] = useState<DimensionSchemaConfig[]>(defaultDimensions);
  const [selectedDimension, setSelectedDimension] = useState<DimensionSchemaConfig | null>(null);
  const [mode, setMode] = useState<'list' | 'builder' | 'preview'>('list');
  const [editingDimension, setEditingDimension] = useState<DimensionSchemaConfig | null>(null);

  const handleCreate = () => {
    setEditingDimension(null);
    setSelectedDimension(null);
    setMode('builder');
  };

  const handleEdit = (dimension: DimensionSchemaConfig) => {
    setEditingDimension(dimension);
    setSelectedDimension(dimension);
    setMode('builder');
  };

  const handleDelete = (dimensionId: string) => {
    if (confirm('Are you sure you want to delete this dimension?')) {
      setDimensions(dimensions.filter(d => d.id !== dimensionId));
    }
  };

  const handlePreview = (dimension: DimensionSchemaConfig) => {
    setSelectedDimension(dimension);
    setMode('preview');
  };

  const handleSave = (dimension: DimensionSchemaConfig) => {
    if (editingDimension) {
      // Update existing
      setDimensions(dimensions.map(d => d.id === dimension.id ? dimension : d));
    } else {
      // Create new
      setDimensions([...dimensions, dimension]);
    }
    setMode('list');
    setEditingDimension(null);
  };

  const handleCancel = () => {
    setMode('list');
    setEditingDimension(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Database className="h-8 w-8" />
            Dimension Management
          </h1>
          <p className="text-muted-foreground mt-2">
            Kelola dimensi data warehouse untuk dashboard eksekutif pemerintahan
          </p>
        </div>
        {mode === 'list' && (
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Dimension
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {mode === 'list' && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Dimensions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dimensions.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">System Dimensions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dimensions.filter(d => d.isSystem).length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Custom Dimensions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dimensions.filter(d => !d.isSystem).length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Fields</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {dimensions.reduce((sum, d) => sum + d.fields.length, 0)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      {mode === 'list' && (
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList>
            <TabsTrigger value="all">All Dimensions</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
            <TabsTrigger value="schema">Schema Config</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            <DimensionList
              dimensions={dimensions}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={handlePreview}
            />
          </TabsContent>

          <TabsContent value="system" className="space-y-4">
            <DimensionList
              dimensions={dimensions.filter(d => d.isSystem)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={handlePreview}
            />
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            <DimensionList
              dimensions={dimensions.filter(d => !d.isSystem)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={handlePreview}
            />
          </TabsContent>

          <TabsContent value="schema" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Schema Configuration (JSON)</CardTitle>
                <CardDescription>
                  Export atau import konfigurasi dimensi dalam format JSON
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg overflow-auto max-h-96 text-sm">
                  {JSON.stringify(dimensions, null, 2)}
                </pre>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => {
                    const blob = new Blob([JSON.stringify(dimensions, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'dimension-schema.json';
                    a.click();
                  }}>
                    <Settings className="h-4 w-4 mr-2" />
                    Export Schema
                  </Button>
                  <Button variant="outline" onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.json';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          try {
                            const imported = JSON.parse(e.target?.result as string);
                            setDimensions(imported);
                            alert('Schema imported successfully!');
                          } catch (err) {
                            alert('Invalid JSON file');
                          }
                        };
                        reader.readAsText(file);
                      }
                    };
                    input.click();
                  }}>
                    <FolderTree className="h-4 w-4 mr-2" />
                    Import Schema
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {mode === 'builder' && (
        <DimensionBuilder
          dimension={editingDimension}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}

      {mode === 'preview' && selectedDimension && (
        <DimensionPreview
          dimension={selectedDimension}
          onEdit={() => handleEdit(selectedDimension)}
          onBack={() => setMode('list')}
        />
      )}
    </div>
  );
}
